"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import AnimalIcon from "@/assets/animal-icon.svg"; // Import the SVG icon

interface FunnelProgressIndicatorProps {
  getLocalizedPath: (pathKey: string) => string;
}

const FunnelProgressIndicator: React.FC<FunnelProgressIndicatorProps> = ({ getLocalizedPath }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;

  const steps = [
    {
      id: "petDetails",
      labelKey: "funnelProgress.step1",
      paths: [getLocalizedPath('routes.home'), getLocalizedPath('routes.petDetails')],
    },
    {
      id: "planAndCoverage",
      labelKey: "funnelProgress.step2",
      paths: [getLocalizedPath('routes.productVariantSelection'), getLocalizedPath('routes.productCustomization')],
    },
    {
      id: "contactDetails",
      labelKey: "funnelProgress.step3", 
      paths: [getLocalizedPath('routes.addressDetails')],
    },
    {
      id: "confirmation",
      labelKey: "funnelProgress.step4",
      paths: [getLocalizedPath('routes.confirmation')],
    },
  ];

  let activeStepIndex = steps.findIndex(step =>
    step.paths.includes(currentPath)
  );

  if (activeStepIndex === -1) {
    activeStepIndex = steps.findIndex(step =>
      step.paths.some(path => currentPath.startsWith(path) && path !== '/')
    );
  }

  // Default to the first step if no active step is found
  const currentStepIndex = activeStepIndex !== -1 ? activeStepIndex : 0;

  const ICON_WIDTH_REM = 3.5; // w-14 / h-14

  const getIconLeft = (index: number, totalSteps: number) => {
    if (totalSteps <= 1) return "0%";

    const percentage = (index / (totalSteps - 1)) * 100;

    // First step: align to left border
    if (index === 0) {
      return "0%";
    }

    // Last step: clamp so the icon stays fully inside the bar
    if (index === totalSteps - 1) {
      return `calc(100% - ${ICON_WIDTH_REM}rem)`;
    }

    // Middle steps: center the icon on the position
    return `calc(${percentage}% - ${ICON_WIDTH_REM / 2}rem)`;
  };


  return (
    <div className={cn("w-full mx-auto mb-4 relative h-24 select-none", currentPath === getLocalizedPath('routes.productVariantSelection') ? "max-w-6xl" : "max-w-4xl")}>
      {/* Top row: Numbers and Labels */}
      <nav aria-label={t("a11y.progress")}>
      <ol className="flex justify-between items-end mb-4">
        {steps.map((step, index) => (
          <li key={step.id} aria-current={index === currentStepIndex ? "step" : undefined} className="flex items-baseline">
            <span
              className={cn(
                "font-bold mr-1",
                index === currentStepIndex ? "text-primary text-4xl" : "text-muted-foreground text-lg"
              )}
            >
              {index + 1}
            </span>
            <span
              className={cn(
                "text-md",
                index === currentStepIndex ? "text-primary font-bold" : "text-muted-foreground",
                // Hidden visually (not from screen readers) on mobile for non-active steps
                index !== currentStepIndex && "sr-only sm:not-sr-only"
              )}
            >
              {t(step.labelKey)}
            </span>
          </li>
        ))}
      </ol>
      </nav>

      {/* Progress Line */}
      <div className="relative h-0.5 bg-primary w-full overflow-visible">
        {steps.length > 1 && (
          <div
            className="pointer-events-none absolute -bottom-8 transition-all duration-300 z-10"
            style={{
              left: getIconLeft(currentStepIndex, steps.length),
            }}
          >
            <img src={AnimalIcon} alt="" aria-hidden="true" className="block w-14 h-14" />
          </div>
        )}
      </div>

    </div>
  );
};

export default FunnelProgressIndicator;
